import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Dropdown, Form, Modal, Row } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/client";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";

const staffTaskStatusOptions = ["Pending", "In Progress", "Completed", "Cancelled"];

function getStatusBadgeClass(status) {
  switch (status) {
    case "Completed":
      return "status-badge status-badge-completed";
    case "In Progress":
      return "status-badge status-badge-progress";
    case "On Hold":
      return "status-badge status-badge-hold";
    case "Cancelled":
      return "status-badge status-badge-cancelled";
    case "Pending":
    default:
      return "status-badge status-badge-pending";
  }
}

function getStatusOptionClass(status) {
  switch (status) {
    case "Completed":
      return "text-success";
    case "In Progress":
      return "text-primary";
    case "Cancelled":
      return "text-danger";
    case "Pending":
    default:
      return "text-secondary";
  }
}

function formatDaysOverDue(value) {
  if (!value || value <= 0) {
    return "-";
  }

  return `${value} day${value === 1 ? "" : "s"}`;
}

function formatDate(value) {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleDateString();
}

function downloadBlob(blob, fileName) {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName || "staff-task-file";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
}

function getFileIconClass(fileName, fileType) {
  const normalizedName = String(fileName || "").toLowerCase();
  const normalizedType = String(fileType || "").toLowerCase();

  if (normalizedName.endsWith(".pdf") || normalizedType.includes("pdf")) {
    return "bi-file-earmark-pdf text-danger";
  }
  if (
    normalizedName.endsWith(".doc") ||
    normalizedName.endsWith(".docx") ||
    normalizedType.includes("word") ||
    normalizedType.includes("officedocument.wordprocessingml")
  ) {
    return "bi-file-earmark-word text-primary";
  }
  if (
    normalizedName.endsWith(".xls") ||
    normalizedName.endsWith(".xlsx") ||
    normalizedType.includes("excel") ||
    normalizedType.includes("spreadsheetml")
  ) {
    return "bi-file-earmark-excel text-success";
  }
  if (
    normalizedName.endsWith(".ppt") ||
    normalizedName.endsWith(".pptx") ||
    normalizedType.includes("presentation")
  ) {
    return "bi-file-earmark-ppt text-warning";
  }

  return "bi-file-earmark-text text-muted";
}

function getUploadErrorMessage(err, fallbackMessage) {
  if (err.response?.status === 413) {
    return "This file is too large for the server upload limit. Please upload a smaller file or ask the administrator to increase the upload limit.";
  }

  return err.response?.data?.error || fallbackMessage;
}

function StaffTaskDetailPage() {
  const history = useHistory();
  const { uuid } = useParams();
  const [task, setTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [fileUploadForm, setFileUploadForm] = useState({
    file: null,
    saving: false
  });
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateForm, setUpdateForm] = useState({
    content: "",
    saving: false
  });
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchPageData();
  }, [uuid]);

  const assignedToName = useMemo(() => {
    const matchedUser = users.find((user) => String(user.id) === String(task?.user_id || ""));
    if (!matchedUser) {
      return task?.user_id || "-";
    }

    return [matchedUser.firstname, matchedUser.lastname].filter(Boolean).join(" ") || matchedUser.username;
  }, [task, users]);

  const assignedByName = useMemo(() => {
    const matchedUser = users.find((user) => String(user.id) === String(task?.assigned_by_id || ""));
    if (!matchedUser) {
      return task?.assigned_by_id || "-";
    }

    return [matchedUser.firstname, matchedUser.lastname].filter(Boolean).join(" ") || matchedUser.username;
  }, [task, users]);

  async function fetchPageData() {
    setLoading(true);
    setError("");

    try {
      const [taskResponse, usersResponse, updatesResponse] = await Promise.all([
        api.get(`/staff-tasks/${uuid}`),
        api.get("/users"),
        api.get(`/staff-tasks/${uuid}/updates`)
      ]);
      setTask(taskResponse.data);
      setUsers(usersResponse.data || []);
      setUpdates(updatesResponse.data || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load staff task details.");
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);

    try {
      await api.delete(`/staff-tasks/${uuid}`);
      toast.success("Staff task deleted");
      history.push("/staff-tasks");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete staff task.");
      setDeleting(false);
    }
  }

  function openFileUploadModal() {
    setFileUploadForm({
      file: null,
      saving: false
    });
    setShowFileUploadModal(true);
  }

  function closeFileUploadModal() {
    setShowFileUploadModal(false);
    setFileUploadForm({
      file: null,
      saving: false
    });
  }

  function openUpdateModal() {
    setUpdateForm({
      content: "",
      saving: false
    });
    setShowUpdateModal(true);
  }

  function closeUpdateModal() {
    setShowUpdateModal(false);
    setUpdateForm({
      content: "",
      saving: false
    });
  }

  async function handleCreateUpdate() {
    if (!updateForm.content.trim()) {
      toast.error("Update comment is required.");
      return;
    }

    setUpdateForm((current) => ({
      ...current,
      saving: true
    }));

    try {
      await api.post(`/staff-tasks/${uuid}/updates`, {
        content: updateForm.content.trim()
      });
      toast.success("Staff task update added");
      await fetchPageData();
      closeUpdateModal();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save staff task update.");
      setUpdateForm((current) => ({
        ...current,
        saving: false
      }));
    }
  }

  async function handleDeleteUpdate(updateUuid) {
    try {
      await api.delete(`/staff-tasks/${uuid}/updates/${updateUuid}`);
      toast.success("Staff task update deleted");
      await fetchPageData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete staff task update.");
    }
  }

  async function handleFileUpload() {
    if (!fileUploadForm.file) {
      toast.error("Please choose a file to upload.");
      return;
    }

    setFileUploadForm((current) => ({
      ...current,
      saving: true
    }));

    const formData = new FormData();
    formData.append("file", fileUploadForm.file);

    try {
      await api.post(`/staff-tasks/${uuid}/files`, formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("Staff task file uploaded");
      await fetchPageData();
      closeFileUploadModal();
    } catch (err) {
      toast.error(getUploadErrorMessage(err, "Failed to upload staff task file."));
      setFileUploadForm((current) => ({
        ...current,
        saving: false
      }));
    }
  }

  async function handleFileDownload(file) {
    try {
      const response = await api.get(`/staff-tasks/${uuid}/files/${file.uuid}`, {
        responseType: "blob"
      });
      downloadBlob(response.data, file.file_name);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to download staff task file.");
    }
  }

  async function handleStatusChange(nextStatus) {
    if (!task || !nextStatus || nextStatus === task.status) {
      return;
    }

    setStatusSaving(true);

    try {
      await api.put(`/staff-tasks/${uuid}`, {
        user_id: task.user_id,
        task_given: task.task_given,
        date_assigned: task.date_assigned,
        date_due: task.date_due || null,
        status: nextStatus,
        report_required: task.report_required
      });
      toast.success(`Task marked as ${nextStatus}`);
      await fetchPageData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update task status.");
    } finally {
      setStatusSaving(false);
    }
  }

  function renderDetailItem(label, value) {
    return {
      label,
      value: value || "-"
    };
  }

  function renderDetailRows(items) {
    const rows = [];

    for (let index = 0; index < items.length; index += 2) {
      const left = items[index];
      const right = items[index + 1];

      rows.push(
        <tr key={left.label}>
          <th>{left.label}</th>
          <td>{left.value}</td>
          {right ? (
            <>
              <th>{right.label}</th>
              <td>{right.value}</td>
            </>
          ) : (
            <>
              <th className="staff-task-detail-empty"></th>
              <td className="staff-task-detail-empty"></td>
            </>
          )}
        </tr>
      );
    }

    return rows;
  }

  return (
    <div>
      <PageHeader
        title="Staff Task Details"
        subtitle={
          task ? (
            <div className="activity-detail-hero">
              <div className="activity-detail-title">{task.task_given}</div>
            </div>
          ) : (
            "Loading staff task details..."
          )
        }
        action={
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={() => history.push("/staff-tasks")}>
              Back to staff tasks
            </Button>
            <Button variant="primary" onClick={() => history.push(`/staff-tasks/${uuid}/edit`)}>
              Edit task
            </Button>
            <Button variant="outline-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting..." : "Delete task"}
            </Button>
          </div>
        }
      />

      {loading ? <LoadingState label="Loading staff task details..." /> : null}
      {error ? <Alert variant="danger">{error}</Alert> : null}

      {!loading && task ? (
        <div className="d-flex flex-column gap-4">
          <div className="content-card activity-detail-section">
            <h5 className="mb-3">Core details</h5>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0 staff-task-detail-table">
                <tbody>
                  {renderDetailRows([
                    renderDetailItem("Task ID", String(task.id)),
                    renderDetailItem("Assigned To", assignedToName),
                    renderDetailItem("Assigned By", assignedByName),
                    renderDetailItem(
                      "Status",
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className={getStatusBadgeClass(task.status)}>{task.status}</span>
                        <Dropdown align="end">
                          <Dropdown.Toggle
                            variant="link"
                            size="sm"
                            className="d-inline-flex align-items-center justify-content-center p-0 border-0 text-muted shadow-none"
                            disabled={statusSaving}
                            id="staff-task-status-menu"
                            title="Change status"
                            aria-label="Open status settings"
                          >
                            <i className="bi bi-gear"></i>
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            {staffTaskStatusOptions.map((status) => (
                              <Dropdown.Item
                                key={status}
                                active={status === task.status}
                                onClick={() => handleStatusChange(status)}
                                className="d-flex align-items-center justify-content-between gap-3"
                              >
                                <span className="d-inline-flex align-items-center gap-2">
                                  <i className={`bi bi-circle-fill small ${getStatusOptionClass(status)}`}></i>
                                  <span>{status}</span>
                                </span>
                                {status === task.status ? <i className="bi bi-check2"></i> : null}
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                        {statusSaving ? <span className="small text-muted">Updating...</span> : null}
                      </div>
                    ),
                    renderDetailItem("Days overdue", formatDaysOverDue(task.days_over_due)),
                    renderDetailItem("Date assigned", task.date_assigned || "-"),
                    renderDetailItem("Due date", task.date_due || "-"),
                    renderDetailItem("Date completed", task.date_completed || "-"),
                    renderDetailItem("Report required", task.report_required ? "Yes" : "No"),
                    renderDetailItem("Created", formatDate(task.created_at)),
                    renderDetailItem("Last updated", formatDate(task.last_updated))
                  ])}
                </tbody>
              </table>
            </div>
          </div>

          <div className="content-card activity-detail-section">
            <div className="d-flex justify-content-between align-items-center mb-3 gap-3">
              <h5 className="mb-0">Files</h5>
              <Button size="sm" onClick={openFileUploadModal}>
                Upload file
              </Button>
            </div>
            {task.files?.length ? (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>File name</th>
                      <th>Uploaded On</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {task.files.map((file) => (
                      <tr key={file.uuid}>
                        <td>
                          <button
                            type="button"
                            className="staff-task-file-name staff-task-file-button"
                            onClick={() => handleFileDownload(file)}
                          >
                            <i className={`bi ${getFileIconClass(file.file_name, file.file_type)}`}></i>
                            <span>{file.file_name || "-"}</span>
                          </button>
                        </td>
                        <td>{formatDate(file.created_at)}</td>
                        <td className="text-end">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="d-inline-flex align-items-center justify-content-center"
                            onClick={() => handleFileDownload(file)}
                            aria-label={`Download ${file.file_name || "file"}`}
                            title="Download file"
                          >
                            <i className="bi bi-download"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mb-0 text-muted">No files uploaded for this task yet.</p>
            )}
          </div>

          <div className="content-card table-card activity-detail-section">
            <div className="d-flex justify-content-between align-items-center mb-3 gap-3">
              <h5 className="mb-0">Staff task updates</h5>
              <Button size="sm" onClick={openUpdateModal}>
                Add update
              </Button>
            </div>

            {updates.length ? (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th className="staff-task-update-text-cell">Update</th>
                      <th className="staff-task-update-user-cell">Updated By</th>
                      <th className="staff-task-update-date-cell">Updated On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {updates.map((update) => (
                      <tr key={update.uuid}>
                        <td className="staff-task-update-text-cell">{update.content}</td>
                        <td className="staff-task-update-user-cell">
                          {update.firstname} {update.lastname}
                        </td>
                        <td className="staff-task-update-date-cell">{formatDate(update.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mb-0 text-muted">No staff task updates have been added yet.</p>
            )}
          </div>
        </div>
      ) : null}

      <Modal show={showUpdateModal} onHide={closeUpdateModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add staff task update</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-label">Update comment</label>
          <Form.Control
            as="textarea"
            rows={5}
            value={updateForm.content}
            onChange={(event) =>
              setUpdateForm((current) => ({
                ...current,
                content: event.target.value
              }))
            }
            placeholder="Enter staff task update or comment"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeUpdateModal}>
            Cancel
          </Button>
          <Button onClick={handleCreateUpdate} disabled={updateForm.saving}>
            {updateForm.saving ? "Saving..." : "Save update"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showFileUploadModal} onHide={closeFileUploadModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Upload staff task file</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-label">Choose file</label>
          <Form.Control
            type="file"
            onChange={(event) =>
              setFileUploadForm((current) => ({
                ...current,
                file: event.target.files?.[0] || null
              }))
            }
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeFileUploadModal}>
            Cancel
          </Button>
          <Button onClick={handleFileUpload} disabled={fileUploadForm.saving}>
            {fileUploadForm.saving ? "Uploading..." : "Upload file"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default StaffTaskDetailPage;
